import { DestroyRef, Injectable, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import { REALTIME_EVENTS } from '../../../core/hubs/realtime/config/realtime-events.config';
import { RealtimeHubService } from '../../../core/hubs/realtime/realtime-hub.service';
import type { NotificationPayload } from '../../../core/hubs/realtime/models/notification.model';
import {
  ToasterService,
  type MenuCatalogImportFailureIssue,
} from '../../../core/services/toaster.service';
import {
  IMPORT_SOURCES,
  type MenuCatalogImportNotificationData,
} from '../models/menu-catalog-import.model';

@Injectable({ providedIn: 'root' })
export class MenuCatalogRealtimeService {
  private readonly destroyRef = inject(DestroyRef);
  private readonly realtimeHub = inject(RealtimeHubService);
  private readonly toaster = inject(ToasterService);

  constructor() {
    this.realtimeHub
      .on$(REALTIME_EVENTS.Notification)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((payload) => this.onRealtimeImportNotification(payload));
  }

  private isAppTabVisible(): boolean {
    if (typeof document === 'undefined') return false;
    return document.visibilityState === 'visible';
  }

  private onRealtimeImportNotification(payload: NotificationPayload): void {
    // In-app toast when this tab is visible; background tabs use DesktopNotificationsService.
    if (!this.isAppTabVisible()) return;

    const source = String(payload.source ?? '').trim();
    if (!source || !IMPORT_SOURCES.has(source)) {
      return;
    }

    const data = this.readImportData(payload.data);
    const issues = this.readIssues(data);

    // Failure UI toast (row issues)
    if (issues.length > 0) {
      this.toaster.showMenuCatalogImportFailure({
        kind: 'menuCatalogImportFailure',
        title: String(payload.title ?? '').trim() || 'Import failed',
        message: String(payload.message ?? '').trim(),
        source,
        batchId: typeof data?.batchId === 'string' ? data.batchId : null,
        issues,
      });
      return;
    }

    // Success or non-row-issue informational message.
    const msg = String(payload.message ?? data?.message ?? '').trim();
    const isFailure = this.isFailureNotification(payload, data);
    if (isFailure) {
      this.toaster.showError(msg || String(payload.title ?? '').trim() || 'Import failed');
      return;
    }

    if (msg) {
      this.toaster.showSuccess(msg);
    }
  }

  private isFailureNotification(
    payload: NotificationPayload,
    data: MenuCatalogImportNotificationData | null,
  ): boolean {
    const normalizedStatus = String(data?.status ?? '').trim().toLowerCase();
    return normalizedStatus === 'failed' || payload.severity >= 3;
  }

  private readImportData(
    raw: Record<string, unknown> | null | undefined,
  ): MenuCatalogImportNotificationData | null {
    if (!raw) return null;
    const rowIssuesRaw = raw['rowIssues'];
    return {
      batchId: typeof raw['batchId'] === 'string' ? raw['batchId'] : undefined,
      importKind: typeof raw['importKind'] === 'string' ? raw['importKind'] : undefined,
      status: typeof raw['status'] === 'string' ? raw['status'] : undefined,
      message: typeof raw['message'] === 'string' ? raw['message'] : undefined,
      rowIssues: Array.isArray(rowIssuesRaw)
        ? (rowIssuesRaw as MenuCatalogImportNotificationData['rowIssues'])
        : undefined,
    };
  }

  private readIssues(data: MenuCatalogImportNotificationData | null): MenuCatalogImportFailureIssue[] {
    const issues = data?.rowIssues;
    if (!issues || !Array.isArray(issues) || issues.length === 0) {
      return [];
    }

    const normalized: MenuCatalogImportFailureIssue[] = [];
    for (const issue of issues) {
      const rowNumber = Number(issue?.rowNumber);
      const msg = String(issue?.message ?? '').trim();
      if (!Number.isFinite(rowNumber) || rowNumber <= 0 || !msg) continue;
      normalized.push({ rowNumber, message: msg });
    }
    return normalized;
  }
}
